import { useState } from "react";
import { BannerCanvas } from "@/components/banner/BannerCanvas";
import { Legend } from "@/components/banner/SlotList";
import { numbering, ratioOf, termStatus, deDate } from "@/lib/bannerLayout";
import { GalleryHorizontal } from "lucide-react";

const fmt = (v, u) => Number(v).toFixed(u === "px" ? 0 : 2).replace(".", ",");
const TONE = { expired: "text-red-600", ending: "text-amber-700", planned: "text-sky-700" };

function BannerCard({ b, today, previewPath }) {
  const { space, slots, blocks } = b;
  const byId = Object.fromEntries(b.partners.map((p) => [p.id, p]));
  const nums = numbering(slots);
  const taken = slots.filter((s) => s.customer_id).length;
  const ordered = [...slots].sort((a, c) => nums[a.id] - nums[c.id]);
  return (
    <article className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3" data-testid={`gallery-banner-${space.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold text-slate-900">{space.name}{space.location && <span className="text-slate-500 font-normal"> · {space.location}</span>}</h3>
        <span className="text-xs text-slate-500">{space.width && space.height ? `${fmt(space.width, space.unit)} × ${fmt(space.height, space.unit)} ${space.unit} · ` : ""}<b className="text-slate-700">{taken} von {slots.length} belegt</b>{slots.length > taken && <span className="text-emerald-700"> · {slots.length - taken} frei</span>}</span>
      </div>
      {slots.length ? <BannerCanvas ratio={ratioOf(space)} slots={slots} blocks={blocks} byId={byId} nums={nums} today={today} readOnly previewPath={previewPath} onSelect={() => {}} onCommit={() => {}} />
        : <p className="text-sm text-slate-400">Noch keine Logofelder geplant.</p>}
      {slots.length > 0 && <ol className="grid gap-x-6 gap-y-0.5 text-sm sm:grid-cols-2 xl:grid-cols-3" data-testid={`gallery-banner-list-${space.id}`}>
        {ordered.map((s) => { const p = byId[s.customer_id]; const st = termStatus(p, today); return (
          <li key={s.id} className="flex items-baseline gap-2">
            <span className="w-12 shrink-0 text-xs text-slate-400">Feld {nums[s.id]}</span>
            {p ? <span className="truncate"><span className="font-medium text-slate-800">{p.name}</span> <span className={`text-xs ${st ? TONE[st.key] : "text-slate-400"}`}>{st ? st.label : p.until ? `bis ${deDate(p.until)}` : ""}</span></span>
              : <span className="text-emerald-700 font-medium">frei</span>}
          </li>
        ); })}
      </ol>}
    </article>
  );
}

export function BannerGallery({ banners, today, previewPath, typeId = "" }) {
  const [sel, setSel] = useState("");
  const list = banners.filter((b) => !typeId || b.space.type_id === typeId);
  if (!list.length) return null;
  const shown = list.filter((b) => !sel || b.space.id === sel);
  return (
    <section className="space-y-4" data-testid="gallery-banners">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2"><GalleryHorizontal size={18} className="text-[#005448]" /> Werbebanner <span className="text-slate-400 font-normal text-sm">({list.length})</span></h2>
        <select value={sel} onChange={(e) => setSel(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="gallery-banner-filter">
          <option value="">Alle Werbebanner</option>
          {list.map((b) => <option key={b.space.id} value={b.space.id}>{[b.space.name, b.space.location].filter(Boolean).join(" · ")}</option>)}
        </select>
      </div>
      {shown.map((b) => <BannerCard key={b.space.id} b={b} today={today} previewPath={previewPath} />)}
      <Legend />
    </section>
  );
}
